'use client';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import termsBG from '../assets/images/terms-and-conditions.webp';
import finalLogo from '../assets/images/logo-white.svg';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useState, useEffect } from 'react';

const styles = {
	bgImage: 'max-h-[80vh] w-full bg-cover bg-center bg-no-repeat relative',
	arrowLeftIcon: 'w-full h-full text-white hover:text-gray transition-colors',
	finalLogo: 'absolute top-[100px] w-[260px] h-[48px] left-1/2 -translate-x-1/2',
	mainHeading:
		'text-white text-[48px] sm:text-[80px] xl:text-[120px] font-bold leading-1.2 lg:leading-1.7',
	container: 'flex-1 gap-[60px] px-6 md:px-[60px] lg:px-[120px] pt-[60px] pb-0',
	containerHeading:
		'mb-6 text-2xl sm:text-2.5xl lg:text-40 font-semibold leading-170 tracking-0 text-dark-blue',
	para: 'text-gray space-y-4 text-sm sm:text-15 lg:text-base xl:text-lg font-semibold leading-170 tracking-0',
	subHeading: 'text-dark-blue text-lg sm:text-xl lg:text-2xl font-semibold mt-6 mb-3',
	sectionList: 'text-gray text-sm sm:text-15 lg:text-base xl:text-lg font-semibold leading-170 tracking-0 ml-4 space-y-2',
	listItem: 'list-disc ml-4',
};

const TermsConditionsPage = ({ onBack }) => {
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	useEffect(() => {
		const img = new Image();
		img.src = termsBG;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	return (
		<div
			className={styles.bgImage}
			style={isImageLoaded ? { backgroundImage: `url(${termsBG})` } : {}}
		>
			{!isImageLoaded && (
				<div className="absolute inset-0">
					<Skeleton
						height="100%"
						width="100%"
						baseColor="#E5E7EB"
						highlightColor="#F3F4F6"
						duration={1.2}
					/>
				</div>
			)}
			{/* Dark overlay */}
			<div className="absolute inset-0 bg-black/50"></div>

			<div className="relative z-10 flex flex-col min-h-screen">
				{/* Header */}
				<div className="flex items-center justify-between">
					<button
						onClick={onBack}
						className="
              absolute
              top-[20px] left-[20px]      /* Mobile */
              sm:top-[30px] sm:left-[40px] /* Small tablets */
              md:top-[40px] md:left-[60px] /* Medium tablets */
              lg:top-[50px] lg:left-[80px] /* Laptops */
              xl:top-[60px] xl:left-[100px] /* Large screens */
              w-[40px] h-[40px] 
              sm:w-[50px] sm:h-[50px]
              xl:w-[60px] xl:h-[60px]
            "
					>
						<ArrowLeftIcon className={styles.arrowLeftIcon} />
					</button>

					<img
						src={finalLogo}
						alt="WriteForm"
						className={styles.finalLogo}
					/>

					{/* Spacer */}
					<div className="w-6"></div>
				</div>

				{/* Main heading */}
				<div className="text-center px-6 mt-[200px] md:mt-[220px]">
					<h1 className={styles.mainHeading}>
						Terms & Conditions
					</h1>
				</div>

				{/* Content container */}
				<div className={styles.container}>
					<div className="bg-white rounded-[50px] p-6 md:p-8 lg:p-12 min-h-[60vh] pb-0">

						<h2 className={styles.containerHeading}>
							<span className="font-bold">Effective Date:</span>{" "}
							<span className="font-medium">December 19, 2025</span>
						</h2>

						<div className={styles.para}>
							<p>
								These Terms of Service ("Terms") govern your access to and use of the WriteForm platform, website, and related services (collectively, the "Services"), operated by WriteForm LLC ("WriteForm," "we," "our," or "us"). By creating an account, purchasing a subscription, or using the Services, you agree to be bound by these Terms. If you do not agree, you must not use the Services.
							</p>

							<h3 className={styles.subHeading}>1. Eligibility and Account Registration</h3>
							<p>
								You must be at least eighteen (18) years old or the age of legal majority in your jurisdiction to use the Services.
							</p>
							<p>
								You agree to provide accurate, current, and complete information when creating an account and to keep your account information up to date. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Account sharing is strictly prohibited. If you suspect unauthorized access, you must notify WriteForm immediately.
							</p>

							<h3 className={styles.subHeading}>2. Scope of the Services</h3>
							<p>
								WriteForm provides academic writing assistance tools, including formatting automation, citation management, and document organization features. The Services are designed to assist with academic formatting and structure only. WriteForm does not guarantee academic approval, institutional acceptance, or compliance with specific submission requirements.
							</p>
							<p>
								WriteForm may modify, suspend, or discontinue any part of the Services at any time.
							</p>

							<h3 className={styles.subHeading}>3. Service Availability and Maintenance</h3>
							<p>
								WriteForm strives to maintain high service availability. However, the Services may be unavailable from time to time due to scheduled maintenance, emergency repairs, system upgrades, or circumstances beyond our reasonable control.
							</p>
							<p>
								WriteForm will use commercially reasonable efforts to provide advance notice of scheduled maintenance that may result in downtime. You acknowledge that temporary interruptions in service may occur and agree that WriteForm shall not be liable for any loss, damage, or inconvenience resulting from such interruptions.
							</p>

							<h3 className={styles.subHeading}>4. Subscriptions, Billing, and Payments</h3>
							<p>
								Certain features of the Services require a paid subscription. Available plans, pricing, and subscription terms are displayed at the time of purchase.
							</p>
							<p>
								Subscriptions are billed according to the selected subscription term, which may be monthly, semester-based, or annual.
							</p>
							<p>
								Payments are processed through a third-party payment processor. WriteForm does not store payment card details.
							</p>

							<h3 className={styles.subHeading}>5. Cancellation, Refunds, and Plan Changes</h3>
							<p>
								<strong>Cancellation</strong>
							</p>
							<p>
								You may cancel your subscription at any time. Cancellation stops future renewals only and does not immediately terminate access. Access remains active through the end of the current subscription term, whether monthly, semester-based, or annual.
							</p>
							{/* <p>
								<strong>Refunds and 48-Hour Grace Period</strong>
							</p>
							<p>
								Refunds are available within forty-eight (48) hours of purchase only if minimal usage has occurred. Minimal usage means:
							</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>No documents have been exported</li>
								<li className={styles.listItem}>No watermark removal has been purchased</li>
								<li className={styles.listItem}>No advanced features have been materially used</li>
							</ul>
							<p>
								If these conditions are met, a full refund may be issued at WriteForm's discretion. After the 48-hour period, all payments are non-refundable.
							</p> */}
							<p>
								Semester-based and annual subscriptions are prepaid, fixed-term subscriptions and are non-refundable after the grace period.
							</p>
							<p>
								<strong>Upgrades</strong>
							</p>
							<p>
								If you upgrade your plan, new features are granted immediately.
							</p>
							<p>
								<strong>Downgrades</strong>
							</p>
							<p>
								If you downgrade your plan, your current plan features remain available until the end of the current subscription term. The downgraded plan takes effect at the start of the next term.
							</p>

							<h3 className={styles.subHeading}>6. Watermarked Exports</h3>
							<p>
								Certain plans include PDF exports that contain a visible watermark on every page. Watermarks may not be removed, altered, obscured, or bypassed. Any attempt to manipulate or circumvent watermarking mechanisms may result in suspension or termination of your account.
							</p>
							<p>
								WriteForm may offer a one-time paid option to remove a watermark from a specific document. This option applies only to that document and does not unlock other paid features.
							</p>

							<h3 className={styles.subHeading}>7. User Content, Academic Responsibility, and Intellectual Property</h3>
							<p>
								You retain ownership of all documents and content you create or upload using the Services ("User Content"). WriteForm does not claim authorship or ownership of your work.
							</p>
							<p>
								By using the Services, you grant WriteForm a limited, non-exclusive license to store, process, and display User Content solely for the purpose of providing the Services.
							</p>
							<p>
								WriteForm is not responsible for plagiarism, the accuracy of sources, or the compliance of any submitted work with institutional policies, academic integrity requirements, or codes of conduct. You are solely responsible for ensuring that your work complies with applicable academic standards and institutional rules.
							</p>
							<p>
								All software, designs, trademarks, and platform content, excluding User Content, are the property of WriteForm or its licensors and may not be copied, modified, reverse-engineered, or otherwise misused.
							</p>

							<h3 className={styles.subHeading}>8. Acceptable Use</h3>
							<p>You agree not to:</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>Use the Services for unlawful purposes</li>
								<li className={styles.listItem}>Violate academic integrity policies or codes of conduct</li>
								<li className={styles.listItem}>Upload malicious code or harmful content</li>
								<li className={styles.listItem}>Attempt to gain unauthorized access to the Services</li>
								<li className={styles.listItem}>Reverse-engineer, decompile, or misuse the platform</li>
								<li className={styles.listItem}>Misrepresent your identity or affiliation</li>
							</ul>
							<p>
								Violations may result in suspension or termination of your account.
							</p>

							<h3 className={styles.subHeading}>9. Document Retention and Data Storage</h3>
							<p>
								Documents may be retained for up to one hundred twenty (120) days after account termination, cancellation, or inactivity, after which they may be archived or permanently deleted. You are solely responsible for maintaining local backups of your work. Backup copies may persist for a limited period for disaster recovery purposes.
							</p>

							<h3 className={styles.subHeading}>10. APA and Institutional Disclaimer</h3>
							<p>
								WriteForm's APA formatting automation is based on publicly available information from the <em>Publication Manual of the American Psychological Association</em> (7th edition) and other public educational resources.
							</p>
							<p>
								WriteForm is not affiliated with, endorsed by, or sponsored by the American Psychological Association or any university, college, or academic institution. Any university-specific formatting logic implemented within the Services is derived from publicly available institutional guidelines and is provided for educational and formatting guidance only.
							</p>

							<h3 className={styles.subHeading}>11. Privacy, Cookies, and Data Protection</h3>
							<p>
								WriteForm uses cookies and similar technologies to improve site performance, enhance the user experience, and analyze site usage. By using the Services, you consent to the use of cookies in accordance with the WriteForm Cookie Policy.
							</p>
							<p>
								The collection and processing of personal data is governed by the WriteForm Privacy Policy, which complies with applicable United States federal law and the European Union General Data Protection Regulation (GDPR).
							</p>

							<h3 className={styles.subHeading}>12. Security Practices</h3>
							<p>
								WriteForm employs commercially reasonable administrative, technical, and organizational measures to protect user accounts and stored documents. However, you are solely responsible for safeguarding your login credentials. WriteForm shall not be liable for any unauthorized access resulting from your failure to maintain adequate security practices.
							</p>

							<h3 className={styles.subHeading}>13. Disclaimers</h3>
							<p>
								The Services are provided "as is" and "as available" without warranties of any kind. WriteForm does not guarantee uninterrupted access, error-free operation, academic success, originality, or institutional acceptance of any document.
							</p>

							<h3 className={styles.subHeading}>14. Limitation of Liability</h3>
							<p>
								To the maximum extent permitted by law, WriteForm will not be liable for any indirect, incidental, special, consequential, or punitive damages. WriteForm's total liability for any claim will not exceed the greater of $100 USD or the amount you paid to WriteForm during the twelve (12) months preceding the claim.
							</p>

							<h3 className={styles.subHeading}>15. Termination</h3>
							<p>
								WriteForm may suspend or terminate your account if you violate these Terms or applicable law. Upon termination, access to the Services will cease in accordance with the document retention rules stated above.
							</p>

							<h3 className={styles.subHeading}>16. Governing Law and Jurisdiction</h3>
							<p>
								This Agreement shall be governed by and construed in accordance with the laws of the United States, without regard to conflict-of-law principles. The federal and state courts of the United States shall have exclusive jurisdiction over any disputes arising under these Terms.
							</p>
							<p>
								For users located in the European Union, these Terms are subject to applicable GDPR compliance obligations.
							</p>

							<h3 className={styles.subHeading}>17. Changes to These Terms</h3>
							<p>
								WriteForm may update these Terms from time to time. Material changes will be communicated through the Services or by email. Continued use of the Services after changes take effect constitutes acceptance of the updated Terms.
							</p>

							<h3 className={styles.subHeading}>18. Contact Information</h3>
							<p>
								For questions regarding these Terms, contact:
							</p>
							<p>📧 legal@writeformapp.com</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TermsConditionsPage;